import { config } from '@iheartjane/config';
import { Flex, Typography, useToast } from '@iheartjane/reefer';
import type { FontFamily, Nullable } from '@iheartjane/types';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { FontPicker } from '@jane/business-admin/components/premium/FontPicker';
import { TextWithFont } from '@jane/business-admin/components/premium/TextWithFont';
import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import { businessPaths, toLabeledFontOption } from '@jane/business-admin/util';
import { useGoogleFonts } from '@jane/shared-ecomm/data-access';
import { loadFont } from '@jane/shared/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';

import styles from './premiumFonts.module.css';

export const PremiumFonts = () => {
  const navigate = useNavigate();
  const fontQuery = useGoogleFonts({ apiKey: config.googleFontsKey });
  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isPending } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );
  const toast = useToast();

  const supportedFontFamilies = useMemo(
    () => fontQuery.data ?? [],
    [fontQuery.data]
  );

  const formMethods = useForm<{
    fontUpdates: {
      body: Nullable<FontFamily>;
      heading: Nullable<FontFamily>;
    };
  }>({
    defaultValues: {
      fontUpdates: {
        heading: bloomMenuQuery.data?.theme?.bloom?.fonts?.heading,
        body: bloomMenuQuery.data?.theme?.bloom?.fonts?.body,
      },
    },
  });
  const { control } = formMethods;
  const fontUpdates = useWatch({ control, name: 'fontUpdates' });

  useEffect(() => {
    loadFont({
      fontFamily: fontUpdates.heading?.font_family,
      url: fontUpdates.heading?.url,
    });
    loadFont({
      fontFamily: fontUpdates.body?.font_family,
      url: fontUpdates.body?.url,
    });
  }, [JSON.stringify(fontUpdates)]);

  const publish = useCallback(() => {
    updateBloomMenu(
      {
        theme: {
          ...bloomMenuQuery.data?.theme?.bloom,
          fonts: {
            heading: fontUpdates.heading ?? null,
            body: fontUpdates.body ?? null,
          },
        },
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          toast.add({
            label: 'Menu updated!',
            variant: 'success',
          });
          navigate(businessPaths.premium());
        },
      }
    );
  }, [
    bloomMenuQuery.data?.theme?.bloom,
    toast,
    updateBloomMenu,
    fontUpdates,
    navigate,
  ]);

  const preview = useCallback(() => {
    updateBloomMenu(
      {
        theme_draft: {
          ...bloomMenuQuery.data?.theme?.bloom,
          fonts: {
            heading: fontUpdates.heading ?? null,
            body: fontUpdates.body ?? null,
          },
        },
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          if (!bloomMenuQuery.data?.menu_url) {
            return;
          }
          const previewUrl = new URL(bloomMenuQuery.data.menu_url);
          previewUrl.searchParams.set('draft', 'true');
          const newWindow = window.open(
            previewUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (newWindow) {
            newWindow.opener = null;
          }
        },
      }
    );
  }, [bloomMenuQuery.data, fontUpdates, toast, updateBloomMenu]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isPending}
      onSubmit={publish}
      preview={{
        onClick: preview,
        disable: isPending,
      }}
      title="Fonts"
    >
      {bloomMenuQuery.isSuccess && (
        <Flex
          className={styles['borderedSection']}
          background="grays-white"
          p={40}
          flexDirection="row"
          gap={48}
        >
          <Flex flexDirection="column" gap={24} width="50%">
            <Flex flexDirection="column" gap={8}>
              <Typography variant="body-semibold">Headings</Typography>

              <Controller
                control={control}
                name="fontUpdates.heading"
                render={({ field }) => (
                  <FontPicker
                    activeFontFamily={toLabeledFontOption(field.value)}
                    defaultFontFamily={{
                      label: 'Euclid Circular B (Default)',
                      value: 'Euclid Circular B',
                    }}
                    fontFamilies={supportedFontFamilies}
                    label="heading-font"
                    onChange={(font) => {
                      field.onChange(font);
                    }}
                  />
                )}
              />
            </Flex>
            <Flex flexDirection="column" gap={8}>
              <Typography variant="body-semibold">Paragraph</Typography>
              <Controller
                control={control}
                name="fontUpdates.body"
                render={({ field }) => (
                  <FontPicker
                    activeFontFamily={toLabeledFontOption(field.value)}
                    defaultFontFamily={{
                      label: 'Source Sans Pro (Default)',
                      value: 'Jane Default',
                    }}
                    fontFamilies={supportedFontFamilies}
                    label="body-font"
                    onChange={(font) => {
                      field.onChange(font);
                    }}
                  />
                )}
              />
            </Flex>
          </Flex>

          <Flex flexDirection="column" gap={8} width="50%">
            <Typography variant="body-semibold">Preview</Typography>
            <Flex
              p={16}
              borderRadius="sm"
              background="grays-ultralight"
              flexDirection="column"
              justifyContent="center"
              height="100%"
            >
              <TextWithFont
                fontFamily={fontUpdates.heading?.font_family}
                pb={16}
                variant="h1-regular"
              >
                Heading
              </TextWithFont>

              <TextWithFont fontFamily={fontUpdates.body?.font_family}>
                This is how paragraph text will appear
              </TextWithFont>
            </Flex>
          </Flex>
        </Flex>
      )}
    </ModalPageWrapper>
  );
};
